<template>
	<div>
		<!-- Headline -->
		<div slot="headline" class="fs5 lh125 fw7 c1 mb2">{{ $t("premium") }}</div>

		<j-card class="bg5">
			<!-- Gross Premium -->
			<j-list :list="computedGrossPremium" class="bb1 bc7">
				<div class="flex xs-12 p3" slot="list" slot-scope="{ item }">
					<div class="flex xs-6 xs-left xs-middle fw7 c1 lh125">
						{{ item.label }}
					</div>
					<div class="flex xs-6 xs-right xs-middle lh125">{{ item.value }}</div>
				</div>
			</j-list>

			<!-- Premium Loadings -->
			<div class="xs-12 p3 bb1 bc7" v-if="computedLoadings.length > 0">
				<div class="xs-12 fs3 lh125 fw7 c1">{{ $t("premium_loadings") }}</div>
				<j-list :list="computedLoadings" class="b1 bc7 mt2">
					<div class="flex xs-12 p3" slot="list" slot-scope="{ item }">
						<div class="flex xs-7 xs-left xs-middle fw7 c6 lh125">
							{{ item.label }}
						</div>
						<div class="flex xs-5 xs-right xs-middle lh125">
							<div>{{ item.value }}</div>
						</div>
					</div>
				</j-list>
			</div>

			<!-- Discounts and Taxes -->
			<div class="xs-12 p3 bb1 bc7" v-if="computedPriceBreakdown.length > 0">
				<div slot="header" class="xs-12 fs3 lh125 fw7 pb2">
					{{ $t("price_breakdown") }}
				</div>
				<div slot="body">
					<j-list :list="computedPriceBreakdown" :itemClasses="'mb2 bg4 r2'">
						<div class="flex xs-12 p3" slot="list" slot-scope="{ item }">
							<div class="flex xs-6 xs-left xs-middle fw7 c1 lh125">
								{{ item.label }}
							</div>
							<div class="xs-6 ta3">
								<div class="lh125">{{ item.value }}</div>
								<div class="c6 lh125">{{ item.summary }}</div>
							</div>
						</div>
					</j-list>
				</div>
			</div>

			<!-- Net Premium -->
			<j-list :list="computedNetPremium">
				<div
					class="flex xs-12 p3 bg7--l4 bc7 bt1"
					slot="list"
					slot-scope="{ item }"
				>
					<div class="flex xs-6 xs-left xs-middle fw7 c1 lh125">
						{{ item.label }}
					</div>
					<div class="flex xs-6 xs-right xs-middle lh125 fw7">
						{{ item.value }}
					</div>
				</div>
			</j-list>
		</j-card>
	</div>
</template>

<script>
import JCard from "@/components/JCard"
import JList from "@/components/JList"
import JButton from "@/components/JButton"
import CrmIcon from "@/components/CrmIcon"
import dayjs from "dayjs"
import get from "lodash.get"

export default {
	components: {
		JCard,
		JList,
		JButton,
		CrmIcon
	},

	props: {
		/**
		 * This is a type pricing we get from GraphQl on policies.acceptedOffer.premium
		 */
		pricing: {
			type: Object,
			required: true,
			default: () => ({
				amount: null,
				currencyCode: null,
				formattedPrice: null,
				originalPrice: null,
				formattedOriginalPrice: null,
				appliedDiscounts: {
					code: null,
					ratio: null,
					order: null
				},
				loadings: {
					id: null,
					codeName: {
						code: null,
						name: null
					},
					ratio: null,
					flat: null
				}
			})
		}
	},

	data() {
		return {
			isLoading: false
		}
	},

	computed: {
		computedGrossPremium() {
			return [
				{
					label: this.$t("gross_premium"),
					value: this?.pricing?.formattedOriginalPrice || 0
				}
			]
		},

		computedPriceBreakdown() {
			// Combine taxes and discounts together and order based on order
			const appliedDiscounts = this.pricing.appliedDiscounts
				? this.pricing.appliedDiscounts
				: []
			const appliedTaxes = this.pricing.appliedTaxes
				? this.pricing.appliedTaxes
				: []

			const taxesAndDiscounts = [...appliedDiscounts, ...appliedTaxes].sort(
				(a, b) => a.order - b.order
			)

			const getValue = item => {
				if (!item) return ""
				const ratio = item.ratio ? item.ratio * 100 : null
				const flat = item.formattedFlat ? item.formattedFlat : "0"
				return {
					flat,
					summary: ratio
						? `(${ratio.toFixed(2)}% of ${item.formattedOriginalPrice})`
						: ""
				}
			}

			const loadings = get(this, "pricing.loadings", []) || []

			// prettier-ignore
			return [
				...loadings.map(loading => ({
					label: `${get(loading, "codeName.name", "")}`,
					value: `${get(this, "pricing.currencyCode", "") || ""}${loading?.flat?.toFixed(2)
							.replace(/\d(?=(\d{3})+\.)/g, "$&,") || "0"}`,
					summary: `(${loading.ratio ? loading.ratio.toFixed(2) * 100 : null || "0"}% of ${this.pricing.formattedOriginalPrice})`
				})),
				...taxesAndDiscounts.map(item => ({
					label: item.name ? item.name : item.code,
					value: getValue(item).flat,
					summary: getValue(item).summary
				}))
			]
		},

		computedNetPremium() {
			return [
				{
					label: this.$t("net_premium"),
					value: get(this, "pricing.formattedPrice", null)
				}
			]
		},

		computedLoadings() {
			const loadings = get(this, "pricing.loadings", [])
			if (!loadings) return []

			/**
			 * Helper function that accepts loading type
			 * and decides whether to show flat or ratio
			 */
			const getFlatOrRatio = loading => {
				if (!loading) return ""
				if (loading.ratio) return (loading.ratio * 100).toFixed(2) + "%"
				if (loading.flat)
					return `${get(this, "pricing.currencyCode", "")}${loading.flat}`
			}

			return loadings.map(loading => ({
				id: loading.id,
				label: `${get(loading, "codeName.name", "")}`,
				value: getFlatOrRatio(loading)
			}))
		}
	}
}
</script>
